import { useState, useEffect } from 'react'
import { useNavigate, Link, useSearchParams } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/hooks/useAuth'
import { useTeam } from '@/hooks/useTeam'
import Container from '@/components/common/Container'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Loading from '@/components/common/Loading'
import PortalPrivacySettings from '@/components/dashboard/PortalPrivacySettings'
import AISteeringPanel from '@/components/dashboard/AISteeringPanel'
import TierStatus from '@/components/dashboard/TierStatus'
import ScrimmageManagerSettings from '@/components/dashboard/ScrimmageManagerSettings'
import TeamCalendarSettings from '@/components/dashboard/TeamCalendarSettings'
import SavedSearches from '@/components/scrimmage/SavedSearches'
import WikiSettingsPanel from '@/components/wiki/WikiSettingsPanel'

function Settings() {
  const { user, loading: authLoading, signOut } = useAuth()
  const { team, loading: teamLoading } = useTeam()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState('team')
  const [teamName, setTeamName] = useState('')
  const [managerName, setManagerName] = useState('')
  const [managerEmail, setManagerEmail] = useState('')
  const [calendarUrl, setCalendarUrl] = useState('')
  const [seasonStart, setSeasonStart] = useState('')
  const [saving, setSaving] = useState(false)
  const [success, setSuccess] = useState(false)
  const [showPaymentHistory, setShowPaymentHistory] = useState(false)
  const [usage, setUsage] = useState({ dispatches: 0, emails: 0 })
  const [loadingUsage, setLoadingUsage] = useState(true)
  const [importingCalendar, setImportingCalendar] = useState(false)
  const [importSuccess, setImportSuccess] = useState(false)
  const [importError, setImportError] = useState(null)
  const [unsubscribing, setUnsubscribing] = useState(false)

  useEffect(() => {
    if (team) {
      setTeamName(team.name)
      setManagerName(team.manager_name || '')
      // Default to user's account email if manager_email not set
      setManagerEmail(team.manager_email || user?.email || '')
      setCalendarUrl(team.calendar_feed_url || '')
      setSeasonStart(team.season_start ? new Date(team.season_start).toISOString().split('T')[0] : '')
    }
  }, [team, user])

  // Handle unsubscribe action from URL parameters (for scrimmage alert emails)
  const [unsubscribeSearchId, setUnsubscribeSearchId] = useState(null)

  useEffect(() => {
    const tab = searchParams.get('tab')
    const action = searchParams.get('action')
    const searchId = searchParams.get('search_id')

    if (tab === 'scrimmage' && action === 'unsubscribe' && searchId) {
      setActiveTab('searches')
      setUnsubscribeSearchId(searchId)
      // Clear the URL parameters to avoid re-triggering on refresh
      setSearchParams(new URLSearchParams())
    }
  }, [searchParams, setSearchParams])

  useEffect(() => {
    // Redirect to team setup if no team exists (after both auth and team loading completes)
    if (!authLoading && !teamLoading && !team) {
      navigate('/team-setup')
    }
  }, [authLoading, teamLoading, team, navigate])

  useEffect(() => {
    if (!team?.id) return

    const fetchUsage = async () => {
      setLoadingUsage(true)
      try {
        const { data, error } = await supabase
          .from('usage_summary_view')
          .select('dispatches_sent, emails_sent')
          .eq('team_id', team.id)
          .single()

        if (error && error.code !== 'PGRST116') throw error

        setUsage({
          dispatches: data?.dispatches_sent || 0,
          emails: data?.emails_sent || 0
        })
      } catch (err) {
        console.error('Error fetching usage summary:', err)
      } finally {
        setLoadingUsage(false)
      }
    }

    fetchUsage()
  }, [team?.id])

  const handleSaveTeamSettings = async () => {
    setSaving(true)
    setSuccess(false)
    try {
      // Build update object - don't include name for seed teams
      const updateData = {
        manager_name: managerName.trim() || null,
        manager_email: managerEmail.trim() || null,
        calendar_feed_url: calendarUrl || null,
        season_start: seasonStart ? new Date(seasonStart).toISOString() : null,
        updated_at: new Date().toISOString(),
      }
      
      // Only allow name changes for user_managed teams
      if (team.data_source !== 'seed_auto') {
        updateData.name = teamName
      }

      const { error } = await supabase
        .from('teams')
        .update(updateData)
        .eq('id', team.id)

      if (error) throw error
      setSuccess(true)
      setTimeout(() => setSuccess(false), 3000)
    } catch (err) {
      console.error('Error saving settings:', err)
      alert('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleImportCalendar = async () => {
    if (!calendarUrl) {
      alert('Please enter a calendar URL first')
      return
    }

    setImportingCalendar(true)
    setImportSuccess(false)
    setImportError(null)


    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/import-calendar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          teamId: team.id,
          userId: user.id,
          calendarUrl: calendarUrl
        })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.error || 'Calendar import failed')
      }

      setImportSuccess(true)
      setImportError(null)
      
      // Auto-clear success message after 5 seconds
      setTimeout(() => setImportSuccess(false), 5000)
    } catch (err) {
      setImportError(err.message)
      setImportSuccess(false)
    } finally {
      setImportingCalendar(false)
    }
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/auth')
  }

  if (authLoading || teamLoading) {
    return <Loading text="Loading settings..." />
  }

  if (!team) {
    return null
  }

  const tabs = [
    { id: 'team', label: 'Team Settings' },
    { id: 'privacy', label: 'Portal Privacy' },
    { id: 'wiki', label: 'Wiki Settings' },
    { id: 'ai', label: 'AI Preferences' },
    { id: 'usage', label: 'Usage & Limits' },
    { id: 'billing', label: 'Billing & Payments' },
    { id: 'scrimmage', label: 'Scrimmage Manager' },
    { id: 'searches', label: 'Saved Searches & Alerts' },
    { id: 'calendar', label: 'Team Calendar' },
    { id: 'account', label: 'Account' },
  ]

  return (
    <div className="min-h-screen bg-brand-cream py-8">
      <Container>
        <div className="max-w-4xl mx-auto">
          {/* Navigation Bar */}
          <div className="mb-6">
            <Button
              onClick={() => navigate('/dashboard')}
              variant="secondary"
              className="text-sm"
            >
              ← Back to Dashboard
            </Button>
          </div>

          <h1 className="font-heading text-3xl font-bold mb-6">
            Settings
          </h1>

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-border overflow-x-auto pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                  activeTab === tab.id
                    ? 'text-brand-green border-b-2 border-brand-green'
                    : 'text-brand-slate hover:text-brand-charcoal'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Team Settings Tab */}
          {activeTab === 'team' && (
            <Card>
              <h2 className="font-heading text-xl font-bold mb-4">
                Team Settings
              </h2>
              <div className="space-y-4">
                {/* Team Name - read-only for seed teams */}
                {team.data_source === 'seed_auto' ? (
                  <div>
                    <label className="label">Team Name</label>
                    <div className="mt-1 p-3 bg-gray-50 border border-border rounded text-brand-charcoal">
                      {team.name}
                    </div>
                    <p className="mt-1 text-sm text-brand-slate">
                      Team name is managed by the association database and cannot be changed.
                      {team.association_name && (
                        <span className="block mt-1 text-xs">
                          Association: {team.association_name}
                        </span>
                      )}
                    </p>
                  </div>
                ) : (
                  <Input
                    label="Team Name"
                    value={teamName}
                    onChange={(e) => setTeamName(e.target.value)}
                    disabled={saving}
                  />
                )}
                
                <Input
                  label="Your Name (Team Manager)"
                  placeholder="e.g., John Smith"
                  value={managerName}
                  onChange={(e) => setManagerName(e.target.value)}
                  disabled={saving}
                />
                
                <div>
                  <Input
                    label="Your Email (Reply-To Address)"
                    type="email"
                    placeholder="e.g., coach@example.com"
                    value={managerEmail}
                    onChange={(e) => setManagerEmail(e.target.value)}
                    disabled={saving}
                  />
                  <p className="mt-1 text-sm text-brand-slate">
                    When parents reply to dispatches, their replies will go to this email address. Defaults to your account email ({user?.email}).
                  </p>
                </div>

                <div>
                  <label className="label">Portal URL</label>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <span className="text-brand-slate text-sm whitespace-nowrap">
                      benchbulletin.com/p/
                    </span>
                    <code className="px-3 py-2 bg-gray-50 border border-border rounded text-sm font-mono text-brand-charcoal break-all">
                      {team.url_slug}
                    </code>
                  </div>
                  <p className="mt-1 text-sm text-brand-slate">
                    This URL is permanent and cannot be changed
                  </p>
                </div>

                <div>
                  <Input
                    label="Calendar Feed URL (Optional)"
                    placeholder="https://..."
                    value={calendarUrl}
                    onChange={(e) => setCalendarUrl(e.target.value)}
                    disabled={saving || team.season_tier === 'free'}
                  />
                  <p className="mt-1 text-sm text-brand-slate">
                    Paste your .ics subscription URL from SportsEngine, TeamSnap, Google Calendar, etc. We'll auto-sync daily.
                  </p>
                  {team.season_tier === 'free' && (
                    <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                      Calendar import is a Pro feature. <Link to="/pricing" className="underline">Upgrade to Pro</Link> to import your schedule automatically.
                    </p>
                  )}
                  {team.season_tier === 'paid' && calendarUrl && (
                    <div className="mt-3">
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={handleImportCalendar}
                        disabled={importingCalendar || !calendarUrl}
                        className="w-full md:w-auto"
                      >
                        {importingCalendar ? 'Syncing...' : 'Sync Calendar Now'}
                      </Button>
                      {team.last_calendar_sync && (
                        <p className="mt-2 text-sm text-brand-slate">
                          Last synced: {new Date(team.last_calendar_sync).toLocaleString()}
                        </p>
                      )}
                      {importSuccess && (
                        <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                          ✓ Calendar synced successfully! Events will auto-sync daily.
                        </div>
                      )}
                      {importError && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                          <strong>Import failed:</strong> {importError}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div>
                  <Input
                    label="Season Start Date (Optional)"
                    type="date"
                    value={seasonStart}
                    onChange={(e) => setSeasonStart(e.target.value)}
                    disabled={saving || team.season_tier === 'free'}
                  />
                  <p className="mt-1 text-sm text-brand-slate">
                    Future feature: Used for automatic season end tracking and calendar views
                  </p>
                  {team.season_tier === 'free' && (
                    <p className="mt-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3">
                      Season tracking is a Pro feature.
                    </p>
                  )}
                </div>

                {success && (
                  <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
                    Settings saved successfully!
                  </div>
                )}

                <div className="flex justify-end">
                  <Button
                    variant="primary"
                    onClick={handleSaveTeamSettings}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </div>
            </Card>
          )}

          {/* Portal Privacy Tab */}
          {activeTab === 'privacy' && (
            <PortalPrivacySettings teamId={team.id} />
          )}

          {/* Wiki Settings Tab */}
          {activeTab === 'wiki' && (
            <WikiSettingsPanel teamId={team.id} />
          )}

          {/* AI Preferences Tab */}
          {activeTab === 'ai' && (
            <AISteeringPanel teamId={team.id} />
          )}

          {/* Usage & Limits Tab */}
          {activeTab === 'usage' && (
            <Card>
              <h2 className="font-heading text-xl font-bold mb-4">Usage & Limits</h2>
              <p className="text-sm text-brand-slate mb-6">
                Track how many dispatches and parent emails you've sent this month. Limits reset automatically each month. Keep parent consent records up to date for compliance.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 border border-border rounded-lg bg-brand-cream">
                  <div className="text-sm text-brand-slate mb-1">Dispatches This Month</div>
                  <div className="text-3xl font-bold text-brand-charcoal">
                    {loadingUsage ? '...' : usage.dispatches}
                  </div>
                  <p className="text-xs text-brand-slate mt-2">
                    Free tier allows 2 per month. Pro is unlimited.
                  </p>
                </div>
                <div className="p-4 border border-border rounded-lg bg-brand-cream">
                  <div className="text-sm text-brand-slate mb-1">Parent Emails Sent</div>
                  <div className="text-3xl font-bold text-brand-charcoal">
                    {loadingUsage ? '...' : usage.emails}
                  </div>
                  <p className="text-xs text-brand-slate mt-2">
                    Pro includes unlimited parent emails with consent.
                  </p>
                </div>
              </div>
              {team.season_tier === 'free' && (
                <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <p className="text-sm text-blue-900">
                    Need more? <Link to="/pricing" className="underline font-medium">Upgrade to Pro</Link> for unlimited dispatches, parent emails, and privacy-conscious features like roster controls and consent logging.
                  </p>
                </div>
              )}
              {team.season_tier === 'paid' && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                  <p className="text-sm text-green-900">
                    ✓ You're on Pro! Enjoy unlimited dispatches and parent emails for this season.
                  </p>
                </div>
              )}
            </Card>
          )}

          {/* Billing Tab */}
          {activeTab === 'billing' && (
            <div className="space-y-6">
              {/* Tier Status Card */}
              <TierStatus team={team} />

              {/* Billing Details Card */}
              <Card>
                <h2 className="font-heading text-xl font-bold mb-4">Billing Details</h2>
                <div className="space-y-4">

                <div className="p-4 bg-brand-cream border border-border rounded-lg">
                  <h3 className="font-medium text-brand-charcoal mb-2">Payment History</h3>
                  {team.season_tier === 'paid' ? (
                    <div className="space-y-2">
                      <p className="text-sm text-brand-slate">
                        Latest payment processed via Stripe. Check your email for the receipt.
                      </p>
                      <Button
                        variant="secondary"
                        onClick={() => navigate('/pricing')}
                        className="text-brand-green border-brand-green"
                      >
                        Purchase Another Season
                      </Button>
                    </div>
                  ) : (
                    <p className="text-sm text-brand-slate">
                      You haven't purchased a season yet. Upgrade to unlock unlimited features.
                    </p>
                  )}
                </div>

                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-900">
                    One-time payment. No automatic renewals. Your data stays safe even after the season ends.
                  </p>
                </div>
              </div>
              </Card>
            </div>
          )}

          {/* Scrimmage Manager Tab */}
          {activeTab === 'scrimmage' && (
            <ScrimmageManagerSettings teamId={team.id} team={team} />
          )}

          {/* Saved Searches Tab */}
          {activeTab === 'searches' && (
            <SavedSearches unsubscribeSearchId={unsubscribeSearchId} />
          )}

          {/* Team Calendar Tab */}
          {activeTab === 'calendar' && (
            <TeamCalendarSettings teamId={team.id} team={team} />
          )}

          {/* Account Tab */}
          {activeTab === 'account' && (
            <Card>
              <h2 className="font-heading text-xl font-bold mb-4">
                Account Settings
              </h2>
              <div className="space-y-4">
                <div>
                  <label className="label">Email</label>
                  <div className="mt-1 p-3 bg-gray-50 border border-border rounded">
                    {user?.email || 'Unknown'}
                  </div>
                </div>

                <div>
                  <label className="label">Current Plan</label>
                  <div className="mt-1 p-3 bg-gray-50 border border-border rounded">
                    <span className="font-medium capitalize">{team.season_tier}</span>
                    {team.season_tier === 'paid' && team.season_end && (
                      <span className="text-sm text-brand-slate ml-2">
                        • Season ends {new Date(team.season_end).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>

                <div className="pt-6 border-t border-border">
                  <h3 className="font-medium text-brand-charcoal mb-2">
                    Danger Zone
                  </h3>
                  <Button
                    variant="secondary"
                    onClick={handleSignOut}
                    className="text-red-600 border-red-300 hover:bg-red-50"
                  >
                    Sign Out
                  </Button>
                </div>
              </div>
            </Card>
          )}
        </div>
      </Container>
    </div>
  )
}

export default Settings

